import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { SpinnerAtom, useApiClientsOrNull } from 'design-system/my-lib';
import type { RootState } from '@center-pwa/store/store';

interface ActivityEvent {
  id: string;
  eventType: string;
  actor: string;
  timestamp: string;
}

const ActivitySection: React.FC = () => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);

  const orgId = useMemo(() => {
    if (!identity?.users || !userId) return undefined;
    const currentUser = identity.users.find((u: any) => u.id === userId);
    if (!currentUser) return undefined;
    const org = Array.isArray(currentUser.organization)
      ? currentUser.organization[0]
      : currentUser.organization;
    return (org as any)?.id as string | undefined;
  }, [identity, userId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ActivityEvent[]>([]);

  useEffect(() => {
    if (!apiClients?.graphClient || !orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiClients.graphClient.get(
          `/spheres/${orgId}/timeline?limit=50`
        );
        if (cancelled) return;
        const rawEvents: any[] = response?.data ?? [];
        setEvents(
          rawEvents.map((e) => ({
            id: e.id,
            eventType: e.eventType,
            actor: e.actor,
            timestamp: e.timestamp,
          }))
        );
      } catch {
        if (cancelled) return;
        setError(t('orgSettings.activity.errors.fetchFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, orgId, t]);

  if (loading) {
    return (
      <div className="org-settings-section" role="status">
        <SpinnerAtom size="large" label={t('orgSettings.activity.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-settings-section" role="alert">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="org-settings-section">
      <header className="org-settings-section__header">
        <h2 className="org-settings-section__title">{t('orgSettings.activity.title')}</h2>
        <p className="org-settings-section__subtitle">{t('orgSettings.activity.subtitle')}</p>
      </header>
      {events.length === 0 ? (
        <div className="org-settings-section__empty">
          <i className="pi pi-clock" aria-hidden="true" />
          <p>{t('orgSettings.activity.empty')}</p>
        </div>
      ) : (
        <ul
          className="org-settings-section__list"
          aria-label={t('orgSettings.activity.listLabel')}
        >
          {events.map((event) => (
            <li key={event.id} className="org-settings-section__list-item">
              <span className="org-settings-section__event-type">{event.eventType}</span>
              <span className="org-settings-section__event-actor">
                {event.actor || t('orgSettings.activity.unknownActor')}
              </span>
              <time
                className="org-settings-section__event-time"
                dateTime={event.timestamp}
              >
                {new Date(event.timestamp).toLocaleString()}
              </time>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActivitySection;
